import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MLTranslationState {
  enabled: boolean;
  selectedLanguage: string;
}

@Injectable({
  providedIn: 'root',
})
export class MlTranslationService {
  private readonly STORAGE_KEY = 'ml_translation_state';

  private state$: BehaviorSubject<MLTranslationState>;

  constructor() {
    const savedState = this.loadState();
    this.state$ = new BehaviorSubject<MLTranslationState>(savedState);
  }

  private loadState(): MLTranslationState {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing ML translation state:', e);
      }
    }
    // Use the language from localStorage (set by LangService) as default
    const savedLanguage = localStorage.getItem('language') || 'en';
    return {
      enabled: false,
      selectedLanguage: savedLanguage
    };
  }

  private saveState(state: MLTranslationState): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  public getState(): Observable<MLTranslationState> {
    return this.state$.asObservable();
  }

  public getCurrentState(): MLTranslationState {
    return this.state$.value;
  }

  public setEnabled(enabled: boolean): void {
    const newState = { ...this.state$.value, enabled };
    this.state$.next(newState);
    this.saveState(newState);
  }

  public setLanguage(language: string): void {
    const newState = { ...this.state$.value, selectedLanguage: language };
    this.state$.next(newState);
    this.saveState(newState);
  }

  public isEnabled(): boolean {
    return this.state$.value.enabled;
  }

  public getSelectedLanguage(): string {
    return this.state$.value.selectedLanguage;
  }
}
